/**
 * 게시판 본문(AxiEditor) 에서 **남기는 style 속성과 값** — 프론트 쪽 사본 (2026-10-14).
 *
 * ★ 정본은 서버다 — `apps/chat-server/src/lib/html-sanitize.ts` 의 `BOARD_RICH_STYLES`.
 *   여기는 읽기 화면·저장 직전에 «같은 것»을 거르기 위한 사본이고, 속성 이름 목록이 서버와
 *   같은지는 `allowed-styles.test.ts` 가 서버 소스를 읽어 잠근다.
 *
 * 사용자 결정(2026-10-14): 「style 을 좁게 연다」 — 9-23 의 「style 0건」을 뒤집었다.
 * 근거와 막는 것의 목록은 서버 파일 머리말에 있다.
 */

const STYLE_GUARD = '(?!.*(?:url|expression|image-set|image|var|attr|env|element|paint)\\s*\\()(?!.*(?:javascript|vbscript)\\s*:)';
const re = (body: string, flags = 'i'): RegExp => new RegExp(`^${STYLE_GUARD}(?:${body})$`, flags);

const NUM = '-?\\d{1,5}(?:\\.\\d{1,4})?';
const LEN = `(?:${NUM}(?:px|pt|em|rem|%|vw|vh|cm|mm|ex|ch)?|auto)`;
const LEN4 = `${LEN}(?:\\s+${LEN}){0,3}`;
const COLOR = '(?:#[0-9a-f]{3,8}|rgba?\\(\\s*[\\d.%,\\s/]{1,40}\\)|hsla?\\(\\s*[\\d.%,\\s/deg]{1,40}\\)|[a-z]{3,20})';
const BORDER_STYLE = '(?:none|hidden|solid|dashed|dotted|double|groove|ridge|inset|outset)';
const BORDER = `(?:(?:${LEN}|thin|medium|thick)\\s*)?(?:${BORDER_STYLE}\\s*)?${COLOR}?`;
const KW = (...words: string[]): RegExp => re(words.join('|'));

export const BOARD_RICH_STYLES: Readonly<Record<string, RegExp>> = {
  // 글자
  color: re(COLOR),
  'background-color': re(COLOR),
  background: re(COLOR),
  'font-family': re('[\\p{L}\\p{N}\\s,\'"_.-]{1,200}', 'iu'),
  'font-size': re(`${LEN}|xx-small|x-small|small|medium|large|x-large|xx-large|smaller|larger`),
  'font-weight': KW('normal', 'bold', 'bolder', 'lighter', '[1-9]00'),
  'font-style': KW('normal', 'italic', 'oblique'),
  'text-decoration': re(`(?:none|underline|overline|line-through|solid|double|dotted|dashed|wavy|${COLOR})(?:\\s+(?:underline|overline|line-through|solid|double|dotted|dashed|wavy|${COLOR})){0,3}`),
  'text-align': KW('left', 'right', 'center', 'justify', 'start', 'end'),
  'text-indent': re(LEN),
  'line-height': re(`normal|${NUM}|${LEN}`),
  'letter-spacing': re(`normal|${LEN}`),
  'vertical-align': re(`top|middle|bottom|baseline|sub|super|text-top|text-bottom|${LEN}`),
  'white-space': KW('normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces'),
  'word-break': KW('normal', 'break-all', 'keep-all', 'break-word'),
  'list-style-type': re('[a-z-]{1,30}'),
  // 상자
  margin: re(LEN4), 'margin-top': re(LEN), 'margin-right': re(LEN), 'margin-bottom': re(LEN), 'margin-left': re(LEN),
  padding: re(LEN4), 'padding-top': re(LEN), 'padding-right': re(LEN), 'padding-bottom': re(LEN), 'padding-left': re(LEN),
  border: re(BORDER), 'border-top': re(BORDER), 'border-right': re(BORDER), 'border-bottom': re(BORDER), 'border-left': re(BORDER),
  'border-color': re(`${COLOR}(?:\\s+${COLOR}){0,3}`),
  'border-style': re(`${BORDER_STYLE}(?:\\s+${BORDER_STYLE}){0,3}`),
  'border-width': re(LEN4),
  'border-radius': re(LEN4),
  'border-collapse': KW('collapse', 'separate'),
  'border-spacing': re(LEN4),
  'table-layout': KW('auto', 'fixed'),
  'box-sizing': KW('border-box', 'content-box'),
  width: re(LEN), height: re(LEN),
  'min-width': re(LEN), 'max-width': re(`${LEN}|none`), 'min-height': re(LEN), 'max-height': re(`${LEN}|none`),
  'aspect-ratio': re('\\d{1,4}(?:\\.\\d{1,4})?(?:\\s*/\\s*\\d{1,4}(?:\\.\\d{1,4})?)?|auto'),
  display: KW('block', 'inline', 'inline-block', 'flex', 'inline-flex', 'table', 'table-cell', 'list-item', 'none', '-webkit-box'),
  float: KW('left', 'right', 'none'),
  clear: KW('left', 'right', 'both', 'none'),
  overflow: KW('visible', 'hidden', 'auto', 'scroll', 'clip'),
  'object-fit': KW('contain', 'cover', 'fill', 'none', 'scale-down'),
  'flex-direction': KW('row', 'column', 'row-reverse', 'column-reverse'),
  'align-items': KW('stretch', 'center', 'flex-start', 'flex-end', 'start', 'end', 'baseline'),
  'justify-content': KW('center', 'flex-start', 'flex-end', 'start', 'end', 'space-between', 'space-around', 'space-evenly'),
  // 배치 — 도형·PPTX 슬라이드. ⛔ fixed·sticky 없음. 읽기 화면이 본문 상자 안에 가둔다.
  position: KW('relative', 'absolute', 'static'),
  top: re(LEN), right: re(LEN), bottom: re(LEN), left: re(LEN), inset: re(LEN4),
  'z-index': re('-?\\d{1,3}'),
  isolation: KW('isolate', 'auto'),
  'background-repeat': KW('repeat', 'no-repeat', 'repeat-x', 'repeat-y'),
  'background-attachment': KW('scroll', 'local', 'fixed'),
  cursor: KW('pointer', 'default', 'auto'),
  'pointer-events': KW('none', 'auto'),
  opacity: re('0|1|0?\\.\\d{1,4}'),
  outline: KW('none', '0'),
  filter: re(`drop-shadow\\((?:\\s*${LEN}){2,3}\\s*${COLOR}?\\s*\\)`),
  // 링크 미리보기 카드(`axe-link-card*`, AxiEditor fe4f4dfa5 README) — 제목·설명 두 줄 자르기와 가로 배치.
  // 카드는 인라인 style 로만 모양을 낸다 — 이것들이 지워지면 저장 순간 카드가 깨진다(README 「저장 · 보기 화면」).
  '-webkit-box-orient': KW('vertical', 'horizontal'),
  '-webkit-line-clamp': re('\\d{1,2}|none'),
  'overflow-wrap': KW('normal', 'anywhere', 'break-word'),
  'text-overflow': KW('clip', 'ellipsis'),
  flex: re(`none|auto|${NUM}(?:\\s+${NUM})?(?:\\s+${LEN})?`),
  gap: re(`${LEN}(?:\\s+${LEN})?`),
};

/**
 * 에디터가 다는 data-* 이름(실측). `data-file-id` 는 img 몫이라 따로 둔다.
 * `data-axe-shape-layer` 는 4.0 이름이다(2026-10-16) — 3.x 저장본의 `data-open-axe-shape-layer` 도 그대로 둔다.
 */
export const RICH_DATA_ATTRS = [
  'data-shape', 'data-sid', 'data-page-break', 'data-open-axe-shape-layer',
  'data-pages', 'data-sheet-name', 'data-slide-name', 'data-youtube-id', 'data-yt-cookie',
  'data-axe-shape-layer',
] as const;

export const YT_ID = /^[\w-]{11}$/;
export const YT_EMBED = /^https:\/\/www\.youtube(?:-nocookie)?\.com\/embed\/[\w-]{11}(?:[?#].*)?$/;

/**
 * `style` 속성 값을 선언으로 가른다 — 괄호·따옴표 안의 `;` 는 가르지 않는다
 * (`font-family:"a;b"` · `rgb(1, 2, 3)`).
 */
export function splitDeclarations(style: string): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  let depth = 0;
  let quote: string | null = null;
  let start = 0;
  const push = (end: number) => {
    const decl = style.slice(start, end);
    const i = decl.indexOf(':');
    if (i > 0) out.push([decl.slice(0, i).trim().toLowerCase(), decl.slice(i + 1).trim()]);
  };
  for (let k = 0; k < style.length; k += 1) {
    const c = style[k];
    if (quote) { if (c === quote) quote = null; continue; }
    if (c === '"' || c === "'") { quote = c; continue; }
    if (c === '(') depth += 1;
    else if (c === ')') depth = Math.max(0, depth - 1);
    else if (c === ';' && depth === 0) { push(k); start = k + 1; }
  }
  push(style.length);
  return out;
}

/** 허용목록에 맞는 선언만 남긴 style 값. 남는 것이 없으면 빈 문자열. `!important` 는 뗀다. */
export function filterStyle(style: string): string {
  const kept: string[] = [];
  for (const [prop, rawValue] of splitDeclarations(style)) {
    const value = rawValue.replace(/\s*!important\s*$/i, '');
    const rule = BOARD_RICH_STYLES[prop];
    if (rule && rule.test(value)) kept.push(`${prop}:${value}`);
  }
  return kept.join(';');
}
